// EMSSPhysicsSimulator.jsx
// Side-by-side real-time Newtonian physics: Conventional vs EP-ADCS with EMMS
import { useState, useEffect, useRef, useCallback } from "react";

const MONO = "'JetBrains Mono','Courier New',monospace";
const BG   = "#020810";
const ACC1 = "#00d4ff";   // conventional
const ACC2 = "#00ff9d";   // EP-ADCS
const ACC3 = "#ffd060";   // EMMS mass / warning
const RED  = "#ff4d6d";
const PUR  = "#c084fc";
const PROP_BUDGET = 0.05; // 50 g display budget

const DEFAULTS = {
  HULL_MASS: 8, EMMS_MASS: 1,
  DRAG_FORCE: 43e-6, THRUSTER_FORCE: 5e-3, THRUSTER_MIN_PULSE: 0.05,
  EMMS_MAX_STROKE: 0.032, EMMS_ACTUATE_FORCE: 0.8, EMMS_RESET_FORCE: 0.010,
  ISP: 70, G0: 9.80665,
  DEADBAND: 0.005, SIM_SCALE: 8000, SIM_SPEED: 50,
};

// ── State factories ───────────────────────────────────────────────────────────
function freshConv() {
  return {
    hull_pos:0, hull_vel:0, mass_abs_vel:0,
    prop_used:0, thruster_on:false, thruster_dir:0, thruster_timer:0,
    cycle_count:0, overshoot_mm:0, max_pos:0,
    trail: new Array(120).fill(0), trail_idx:0,
  };
}
function freshEP() {
  return {
    hull_pos:0, hull_vel:0,
    mass_pos:0, mass_vel:0, mass_abs_vel:0,
    prop_used:0, thruster_on:false, thruster_dir:0, thruster_timer:0,
    emms_state:"idle", emms_force_dir:0,
    cycle_count:0, overshoot_mm:0, max_pos:0,
    trail: new Array(120).fill(0), trail_idx:0,
  };
}

// ── Physics: Conventional ─────────────────────────────────────────────────────
function stepConv(s, dt, C) {
  const TM = C.HULL_MASS + C.EMMS_MASS;
  s.hull_vel += (-C.DRAG_FORCE / TM) * dt;
  if (!s.thruster_on) {
    if      (s.hull_pos < -C.DEADBAND) { s.thruster_on=true; s.thruster_dir=+1; s.thruster_timer=C.THRUSTER_MIN_PULSE; }
    else if (s.hull_pos >  C.DEADBAND) { s.thruster_on=true; s.thruster_dir=-1; s.thruster_timer=C.THRUSTER_MIN_PULSE; }
  }
  if (s.thruster_on) {
    s.hull_vel  += (C.THRUSTER_FORCE * s.thruster_dir / TM) * dt;
    s.prop_used += (C.THRUSTER_FORCE * dt) / (C.ISP * C.G0);
    s.thruster_timer -= dt;
    if (s.thruster_timer <= 0) { s.thruster_on=false; s.thruster_timer=0; s.cycle_count++; }
  }
  s.hull_pos += s.hull_vel * dt;
  if (s.hull_pos > C.DEADBAND && s.hull_pos > s.max_pos) s.max_pos = s.hull_pos;
  s.overshoot_mm = s.max_pos * 1000;
  s.trail[s.trail_idx] = s.hull_pos;
  s.trail_idx = (s.trail_idx + 1) % 120;
}

// ── Physics: EP-ADCS ──────────────────────────────────────────────────────────
function stepEP(s, dt, C) {
  const HM = C.HULL_MASS, EM = C.EMMS_MASS, TM = HM + EM;
  s.hull_vel += (-C.DRAG_FORCE / HM) * dt;

  // Keep mass synced to hull in idle (no active force)
  if (s.emms_state === "idle") {
    s.mass_abs_vel = s.hull_vel; s.mass_vel = 0;
  }

  switch (s.emms_state) {
    case "idle":
      if (s.hull_pos < -C.DEADBAND) {
        s.emms_state = "preload"; s.emms_force_dir = -1; s.cycle_count++;
      }
      break;

    case "preload": {
      const F = C.EMMS_ACTUATE_FORCE * s.emms_force_dir;
      s.mass_abs_vel += (F  / EM) * dt;
      s.hull_vel     += (-F / HM) * dt;
      s.mass_vel      = s.mass_abs_vel - s.hull_vel;
      s.mass_pos     += s.mass_vel * dt;
      if (s.mass_pos <= -C.EMMS_MAX_STROKE) {
        s.mass_pos = -C.EMMS_MAX_STROKE;
        s.mass_abs_vel = s.hull_vel; s.mass_vel = 0; // inelastic wall
        s.emms_state = "fire_thruster";
        s.thruster_on=true; s.thruster_dir=+1; s.thruster_timer=C.THRUSTER_MIN_PULSE;
      }
      break;
    }

    case "fire_thruster":
      s.mass_abs_vel = s.hull_vel; s.mass_vel = 0; // mass locked at -stroke
      if (s.thruster_on) {
        s.hull_vel  += (C.THRUSTER_FORCE / TM) * dt;
        s.prop_used += (C.THRUSTER_FORCE * dt) / (C.ISP * C.G0);
        s.thruster_timer -= dt;
        if (s.thruster_timer <= 0) {
          s.thruster_on=false; s.thruster_timer=0;
          s.emms_state="absorb"; s.emms_force_dir=+1;
        }
      }
      break;

    case "absorb": {
      const F = C.EMMS_ACTUATE_FORCE * s.emms_force_dir;
      s.mass_abs_vel += (F  / EM) * dt;
      s.hull_vel     += (-F / HM) * dt;
      s.mass_vel      = s.mass_abs_vel - s.hull_vel;
      s.mass_pos     += s.mass_vel * dt;
      if (s.hull_vel <= 0 || s.mass_pos >= 0) {
        if (s.hull_vel > 0) s.hull_vel = 0;
        s.emms_state = "reset"; s.emms_force_dir = 0;
      }
      break;
    }

    case "reset": {
      const dir = s.mass_pos > 0.0005 ? -1 : s.mass_pos < -0.0005 ? 1 : 0;
      if (dir === 0) {
        s.mass_pos=0; s.mass_vel=0; s.mass_abs_vel=s.hull_vel;
        s.emms_state="idle"; break;
      }
      const F = C.EMMS_RESET_FORCE * dir;
      s.mass_abs_vel += (F  / EM) * dt;
      s.hull_vel     += (-F / HM) * dt;
      s.mass_vel      = s.mass_abs_vel - s.hull_vel;
      s.mass_pos     += s.mass_vel * dt;
      break;
    }
    default: break;
  }

  s.hull_pos += s.hull_vel * dt;
  if (s.hull_pos > C.DEADBAND && s.hull_pos > s.max_pos) s.max_pos = s.hull_pos;
  s.overshoot_mm = s.max_pos * 1000;
  s.trail[s.trail_idx] = s.hull_pos;
  s.trail_idx = (s.trail_idx + 1) % 120;
}

// ── Canvas drawing ────────────────────────────────────────────────────────────
function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x+r,y); ctx.lineTo(x+w-r,y); ctx.arcTo(x+w,y,x+w,y+r,r);
  ctx.lineTo(x+w,y+h-r); ctx.arcTo(x+w,y+h,x+w-r,y+h,r);
  ctx.lineTo(x+r,y+h); ctx.arcTo(x,y+h,x,y+h-r,r);
  ctx.lineTo(x,y+r); ctx.arcTo(x,y,x+r,y,r);
  ctx.closePath();
}
function arrow(ctx, x1,y1, x2,y2, col, lw=1.5) {
  const dx=x2-x1, dy=y2-y1;
  if (Math.hypot(dx,dy) < 2) return;
  const a = Math.atan2(dy,dx), h=6;
  ctx.strokeStyle=col; ctx.lineWidth=lw;
  ctx.beginPath(); ctx.moveTo(x1,y1); ctx.lineTo(x2,y2); ctx.stroke();
  ctx.fillStyle=col;
  ctx.beginPath();
  ctx.moveTo(x2,y2);
  ctx.lineTo(x2-h*Math.cos(a-0.4), y2-h*Math.sin(a-0.4));
  ctx.lineTo(x2-h*Math.cos(a+0.4), y2-h*Math.sin(a+0.4));
  ctx.closePath(); ctx.fill();
}

function drawSat(canvas, s, C, isEP) {
  const ctx = canvas.getContext("2d");
  const W=canvas.width, H=canvas.height;
  const CX=W/2, CY=H/2;
  const accentCol = isEP ? ACC2 : ACC1;
  const bgCol     = isEP ? "#061a10" : "#06111a";

  ctx.fillStyle=BG; ctx.fillRect(0,0,W,H);
  ctx.fillStyle=bgCol+"44"; ctx.fillRect(0,0,W,H);

  // Deadband zone
  const dbPx = C.DEADBAND * C.SIM_SCALE;
  ctx.fillStyle = ACC3+"10";
  ctx.fillRect(CX-dbPx, 0, dbPx*2, H);

  // Reference line
  ctx.strokeStyle="#1a3555"; ctx.lineWidth=1;
  ctx.setLineDash([4,4]);
  ctx.beginPath(); ctx.moveTo(CX,0); ctx.lineTo(CX,H); ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle="#1a3555aa"; ctx.font=`8px ${MONO}`; ctx.textAlign="center";
  ctx.fillText("REF",CX,10);

  // Deadband tick labels
  ctx.fillStyle=ACC3+"55"; ctx.font=`7px ${MONO}`; ctx.textAlign="center";
  ctx.fillText(`+${(C.DEADBAND*1000).toFixed(0)}mm`, CX+dbPx, H-4);
  ctx.fillText(`-${(C.DEADBAND*1000).toFixed(0)}mm`, CX-dbPx, H-4);

  // Trail
  for (let i=1; i<120; i++) {
    const idx = (s.trail_idx - i + 120) % 120;
    const px = CX + s.trail[idx]*C.SIM_SCALE;
    const alpha = Math.floor((1 - i/120)*0.6*255).toString(16).padStart(2,"0");
    ctx.fillStyle = accentCol + alpha;
    ctx.beginPath(); ctx.arc(px, CY+2, 1.5, 0, Math.PI*2); ctx.fill();
  }

  // Hull position
  const hx = CX + s.hull_pos * C.SIM_SCALE;
  const HW=60, HH=30;

  // Thruster glow
  const fwdFiring = s.thruster_on && s.thruster_dir===+1;
  const bwdFiring = s.thruster_on && s.thruster_dir===-1;
  if (fwdFiring) {
    const g=ctx.createRadialGradient(hx+HW/2+8,CY,0, hx+HW/2+8,CY,20);
    g.addColorStop(0,ACC1+"cc"); g.addColorStop(1,ACC1+"00");
    ctx.fillStyle=g; ctx.beginPath(); ctx.arc(hx+HW/2+8,CY,20,0,Math.PI*2); ctx.fill();
  }
  if (bwdFiring) {
    const g=ctx.createRadialGradient(hx-HW/2-8,CY,0, hx-HW/2-8,CY,20);
    g.addColorStop(0,RED+"cc"); g.addColorStop(1,RED+"00");
    ctx.fillStyle=g; ctx.beginPath(); ctx.arc(hx-HW/2-8,CY,20,0,Math.PI*2); ctx.fill();
  }

  // Hull body
  rr(ctx, hx-HW/2, CY-HH/2, HW, HH, 4);
  ctx.fillStyle=isEP?"#0a2040":"#1a3555"; ctx.fill();
  ctx.strokeStyle=accentCol; ctx.lineWidth=1.5; ctx.stroke();

  // Nozzles
  const nW=5, nH=8;
  ctx.fillStyle=bwdFiring?RED:"#223344";
  ctx.fillRect(hx-HW/2-nW, CY-nH/2, nW, nH);
  ctx.fillStyle=fwdFiring?ACC1:"#223344";
  ctx.fillRect(hx+HW/2, CY-nH/2, nW, nH);

  // EMMS mass (EP only)
  if (isEP) {
    const strokPx = C.EMMS_MAX_STROKE * C.SIM_SCALE;
    // track line
    ctx.strokeStyle=ACC3+"30"; ctx.lineWidth=1;
    ctx.beginPath(); ctx.moveTo(hx-strokPx, CY); ctx.lineTo(hx+strokPx, CY); ctx.stroke();
    // stroke limit ticks
    ctx.strokeStyle=ACC3+"50"; ctx.lineWidth=0.5;
    [-strokPx, strokPx].forEach(ox => {
      ctx.beginPath(); ctx.moveTo(hx+ox, CY-6); ctx.lineTo(hx+ox, CY+6); ctx.stroke();
    });
    // mass block
    const mx = hx + s.mass_pos * C.SIM_SCALE;
    const stateColors = {preload:"#ff8c44",absorb:"#ff4d6d",fire_thruster:ACC1,reset:"#ffd06099",idle:ACC3};
    ctx.fillStyle = (stateColors[s.emms_state]||ACC3)+"cc";
    ctx.strokeStyle = ACC3;
    ctx.lineWidth=1;
    ctx.beginPath(); ctx.rect(mx-6, CY-10, 12, 20); ctx.fill(); ctx.stroke();
  }

  // Velocity arrow
  const vPx = s.hull_vel * C.SIM_SCALE * 500;
  arrow(ctx, hx, CY-HH/2-14, hx+vPx, CY-HH/2-14, PUR, 1.5);
  if (Math.abs(vPx)>3) {
    ctx.fillStyle=PUR; ctx.font=`7px ${MONO}`; ctx.textAlign="center";
    ctx.fillText(`${(s.hull_vel*1e6).toFixed(1)}µm/s`, hx+vPx/2, CY-HH/2-22);
  }

  // Label
  ctx.fillStyle=accentCol; ctx.font=`bold 10px ${MONO}`; ctx.textAlign="left";
  ctx.fillText(isEP?"EP-ADCS  (EMMS)":"CONVENTIONAL", 8, 14);

  // EMMS state badge (EP only)
  if (isEP) {
    const stateCol={idle:ACC2,preload:ACC3,fire_thruster:ACC1,absorb:RED,reset:PUR};
    const sc = stateCol[s.emms_state]||ACC3;
    ctx.fillStyle=sc+"22";
    rr(ctx, W-120, 4, 116, 16, 4); ctx.fill();
    ctx.strokeStyle=sc+"66"; ctx.lineWidth=1; rr(ctx, W-120, 4, 116, 16, 4); ctx.stroke();
    ctx.fillStyle=sc; ctx.font=`bold 8px ${MONO}`; ctx.textAlign="center";
    ctx.fillText(s.emms_state.toUpperCase(), W-62, 15);
  }

  // Propellant tank bar
  const tX=W-20, tY=22, tH=H-44, tW=8;
  const fill = Math.max(0, 1-s.prop_used/PROP_BUDGET);
  ctx.fillStyle="#0d1f2e"; ctx.fillRect(tX,tY,tW,tH);
  const fc = fill>0.6?ACC2:fill>0.3?ACC3:RED;
  ctx.fillStyle=fc;
  ctx.fillRect(tX, tY+tH*(1-fill), tW, tH*fill);
  ctx.strokeStyle="#1a3555"; ctx.lineWidth=1; ctx.strokeRect(tX,tY,tW,tH);
  ctx.fillStyle="#aaa"; ctx.font=`6px ${MONO}`; ctx.textAlign="center";
  ctx.fillText("PROP", tX+tW/2, tH+tY+10);

  // CoM drift indicator on reference line
  if (isEP && Math.abs(s.hull_pos)>0.0001) {
    const driftPx = CX + s.hull_pos*C.SIM_SCALE;
    ctx.strokeStyle=ACC2+"80"; ctx.lineWidth=2;
    ctx.beginPath(); ctx.moveTo(driftPx,H-8); ctx.lineTo(driftPx,H); ctx.stroke();
  }
}

// ── Slider helper ─────────────────────────────────────────────────────────────
function Slider({ label, val, min, max, step, fmt, onChange, color=ACC1 }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
      <span style={{ fontFamily:MONO, fontSize:9, color:"#4b7a9a", letterSpacing:1.5, width:160, flexShrink:0 }}>
        {label}
      </span>
      <input type="range" min={min} max={max} step={step} value={val}
        onChange={e=>onChange(parseFloat(e.target.value))}
        style={{ flex:1, accentColor:color, cursor:"pointer" }} />
      <span style={{ fontFamily:MONO, fontSize:11, color, width:80, textAlign:"right" }}>
        {fmt(val)}
      </span>
    </div>
  );
}

// ── Metric cell ───────────────────────────────────────────────────────────────
function Metric({ label, value, color, unit="" }) {
  return (
    <div style={{
      background:"#050f1a", border:"1px solid #0e2236",
      borderRadius:4, padding:"6px 10px",
    }}>
      <div style={{ fontFamily:MONO, fontSize:7, color:"#3a5f7a", letterSpacing:1.5, marginBottom:3 }}>
        {label}
      </div>
      <div style={{ fontFamily:MONO, fontSize:13, color, fontWeight:700 }}>
        {value}<span style={{ fontSize:9, color:"#3a5f7a", marginLeft:3 }}>{unit}</span>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function EMSSPhysicsSimulator() {
  const [playing, setPlaying]   = useState(false);
  const [showConst, setShowConst] = useState(false);
  const [C, setC]               = useState({...DEFAULTS});
  const [disp, setDisp]         = useState({ conv: freshConv(), ep: freshEP(), t:0 });

  const convRef   = useRef(freshConv());
  const epRef     = useRef(freshEP());
  const rafRef    = useRef(null);
  const lastTsRef = useRef(null);
  const canvConv  = useRef(null);
  const canvEP    = useRef(null);
  const frameRef  = useRef(0);
  const CRef      = useRef(C);
  CRef.current    = C;
  const playRef   = useRef(false);
  playRef.current = playing;

  const reset = useCallback(() => {
    convRef.current = freshConv();
    epRef.current   = freshEP();
    lastTsRef.current = null;
    frameRef.current  = 0;
    setDisp({ conv: freshConv(), ep: freshEP(), t:0 });
  }, []);

  // ── RAF loop ────────────────────────────────────────────────────────────────
  const loop = useCallback((ts) => {
    if (!playRef.current) return;
    const dtWall = lastTsRef.current ? Math.min((ts-lastTsRef.current)/1000, 0.05) : 0.016;
    lastTsRef.current = ts;
    const K = CRef.current;
    const dtPhys = dtWall * K.SIM_SPEED;
    const SUB = 20;
    const dt  = dtPhys / SUB;
    const cv  = convRef.current;
    const ep  = epRef.current;
    for (let i=0; i<SUB; i++) { stepConv(cv, dt, K); stepEP(ep, dt, K); }

    // Draw every frame
    if (canvConv.current) drawSat(canvConv.current, cv, K, false);
    if (canvEP.current)   drawSat(canvEP.current,   ep, K, true);

    // Push to React state at ~30fps (every 2nd frame)
    frameRef.current++;
    if (frameRef.current % 2 === 0) {
      setDisp({ conv: {...cv}, ep: {...ep}, t: frameRef.current * dtWall });
    }
    rafRef.current = requestAnimationFrame(loop);
  }, []);

  useEffect(() => {
    if (playing) {
      lastTsRef.current = null;
      rafRef.current = requestAnimationFrame(loop);
    } else {
      cancelAnimationFrame(rafRef.current);
      if (canvConv.current) drawSat(canvConv.current, convRef.current, CRef.current, false);
      if (canvEP.current)   drawSat(canvEP.current,   epRef.current,   CRef.current, true);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, loop]);

  // Initial draw
  useEffect(() => {
    if (canvConv.current) drawSat(canvConv.current, convRef.current, C, false);
    if (canvEP.current)   drawSat(canvEP.current,   epRef.current,   C, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived comparison values ─────────────────────────────────────────────
  const cv = disp.conv, ep = disp.ep;
  const convPropG   = cv.prop_used * 1e6;  // µg → show in µg
  const epPropG     = ep.prop_used * 1e6;
  const saving      = cv.prop_used>0 ? ((cv.prop_used-ep.prop_used)/cv.prop_used*100) : 0;
  const osReduction = cv.overshoot_mm>0 ? ((cv.overshoot_mm-ep.overshoot_mm)/cv.overshoot_mm*100) : 0;
  const convEta     = C.DEADBAND>0 ? (C.DEADBAND + cv.overshoot_mm/1000) / C.DEADBAND : 1;
  const epEta       = C.DEADBAND>0 ? (C.DEADBAND + ep.overshoot_mm/1000) / C.DEADBAND : 1;
  const epBetter    = ep.prop_used <= cv.prop_used;

  // ── Slider updater ────────────────────────────────────────────────────────
  const upd = (key) => (v) => setC(prev => ({...prev,[key]:v}));

  const btnBase = {
    fontFamily:MONO, fontSize:10, letterSpacing:1.5, padding:"7px 20px",
    borderRadius:5, cursor:"pointer", border:"none",
  };

  // ── Layout ────────────────────────────────────────────────────────────────
  return (
    <div style={{ background:BG, color:"#c0d8e8", fontFamily:MONO, padding:"16px 12px", minHeight:"100vh" }}>

      {/* ── Title ── */}
      <div style={{ textAlign:"center", marginBottom:16 }}>
        <div style={{ fontSize:14, fontWeight:700, color:ACC1, letterSpacing:3 }}>
          EMSS PHYSICS SIMULATOR
        </div>
        <div style={{ fontSize:9, color:"#3a5f7a", letterSpacing:2, marginTop:2 }}>
          NEWTONIAN REAL-TIME · CONVENTIONAL vs EP-ADCS
        </div>
      </div>

      {/* ── Controls bar ── */}
      <div style={{
        background:"#050f1a", border:"1px solid #0e2236",
        borderRadius:8, padding:"10px 16px", marginBottom:12,
        display:"flex", gap:12, flexWrap:"wrap", alignItems:"center",
      }}>
        <button
          style={{...btnBase, background: playing?"#2d0f15":"#0d2510",
            border:`1px solid ${playing?RED:ACC2}`, color:playing?RED:ACC2 }}
          onClick={() => setPlaying(p=>!p)}>
          {playing ? "■  PAUSE" : "▶  PLAY"}
        </button>
        <button
          style={{...btnBase, background:"transparent", border:`1px solid #1a3555`, color:"#4b7a9a"}}
          onClick={() => { setPlaying(false); reset(); }}>
          ⟳  RESET
        </button>

        <div style={{ flex:1, minWidth:160 }}>
          <Slider label="SIM SPEED"         val={C.SIM_SPEED}          min={1}    max={500}  step={1}
            fmt={v=>`${v}×`}                onChange={upd("SIM_SPEED")}  color={ACC2} />
          <Slider label="DRAG FORCE"        val={C.DRAG_FORCE*1e6}     min={10}   max={200}  step={1}
            fmt={v=>`${v.toFixed(0)} µN`}   onChange={v=>upd("DRAG_FORCE")(v*1e-6)} color={RED} />
        </div>
        <div style={{ flex:1, minWidth:160 }}>
          <Slider label="DEADBAND"          val={C.DEADBAND*1000}      min={1}    max={20}   step={0.5}
            fmt={v=>`${v.toFixed(1)} mm`}   onChange={v=>upd("DEADBAND")(v*1e-3)}  color={ACC3} />
          <Slider label="THRUSTER MIN PULSE" val={C.THRUSTER_MIN_PULSE*1000} min={10} max={200} step={5}
            fmt={v=>`${v.toFixed(0)} ms`}   onChange={v=>upd("THRUSTER_MIN_PULSE")(v*1e-3)} color={PUR} />
        </div>
      </div>

      {/* ── Satellites side-by-side ── */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:8 }}>
        {/* Conventional */}
        <div>
          <canvas ref={canvConv} width={700} height={200}
            style={{ width:"100%", height:"auto", display:"block",
              border:`1px solid ${ACC1}22`, borderRadius:8,
              background:BG }} />
          {/* Metrics */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:4, marginTop:6 }}>
            <Metric label="HULL POSITION"   value={(cv.hull_pos*1000).toFixed(3)}   unit="mm" color={Math.abs(cv.hull_pos)>C.DEADBAND?RED:ACC1} />
            <Metric label="HULL VELOCITY"   value={(cv.hull_vel*1e6).toFixed(2)}    unit="µm/s" color={ACC1} />
            <Metric label="PROP USED"        value={(cv.prop_used*1e9).toFixed(2)}   unit="ng" color={cv.prop_used>ep.prop_used?RED:ACC1} />
            <Metric label="OVERSHOOT"        value={cv.overshoot_mm.toFixed(3)}      unit="mm" color={cv.overshoot_mm>0?RED:ACC2} />
            <Metric label="EMMS STATE"       value="N/A"                             unit=""   color="#3a5f7a" />
            <Metric label="CYCLE COUNT"      value={cv.cycle_count}                  unit=""   color={ACC1} />
            <Metric label="η FACTOR"         value={convEta.toFixed(3)}              unit=""   color={convEta>1.5?RED:ACC1} />
            <Metric label="MASS POSITION"    value="—"                               unit=""   color="#3a5f7a" />
            <Metric label="THRUSTER"         value={cv.thruster_on?(cv.thruster_dir>0?"FWD↑":"BWD↓"):"OFF"} unit="" color={cv.thruster_on?ACC1:ACC1+"66"} />
          </div>
        </div>

        {/* EP-ADCS */}
        <div>
          <canvas ref={canvEP} width={700} height={200}
            style={{ width:"100%", height:"auto", display:"block",
              border:`1px solid ${ACC2}22`, borderRadius:8,
              background:BG }} />
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:4, marginTop:6 }}>
            <Metric label="HULL POSITION"   value={(ep.hull_pos*1000).toFixed(3)}   unit="mm" color={Math.abs(ep.hull_pos)>C.DEADBAND?RED:ACC2} />
            <Metric label="HULL VELOCITY"   value={(ep.hull_vel*1e6).toFixed(2)}    unit="µm/s" color={ACC2} />
            <Metric label="PROP USED"        value={(ep.prop_used*1e9).toFixed(2)}   unit="ng" color={ep.prop_used<cv.prop_used?ACC2:RED} />
            <Metric label="OVERSHOOT"        value={ep.overshoot_mm.toFixed(3)}      unit="mm" color={ep.overshoot_mm>0?ACC3:ACC2} />
            <Metric label="EMMS STATE"       value={ep.emms_state.toUpperCase()}     unit=""   color={ACC3} />
            <Metric label="CYCLE COUNT"      value={ep.cycle_count}                  unit=""   color={ACC2} />
            <Metric label="η FACTOR"         value={epEta.toFixed(3)}                unit=""   color={epEta<convEta?ACC2:RED} />
            <Metric label="MASS POSITION"    value={(ep.mass_pos*1000).toFixed(2)}   unit="mm" color={ACC3} />
            <Metric label="THRUSTER"         value={ep.thruster_on?"FIRING":"OFF"}   unit=""   color={ep.thruster_on?ACC1:ACC1+"66"} />
          </div>
        </div>
      </div>

      {/* ── Comparison summary bar ── */}
      <div style={{
        background: epBetter ? "#031a0a" : "#1a0308",
        border: `2px solid ${epBetter?ACC2:RED}44`,
        borderTop: `2px solid ${epBetter?ACC2:RED}`,
        borderRadius:8, padding:"12px 18px", marginBottom:12,
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, flexWrap:"wrap", gap:8 }}>
          <div style={{ fontSize:9, color:epBetter?ACC2:RED, letterSpacing:3, fontWeight:700 }}>
            {epBetter ? "✓  EP-ADCS CLAIM VALIDATED — PROPELLANT SAVING CONFIRMED" : "✗  CLAIM UNVALIDATED — EP-ADCS USING MORE PROPELLANT"}
          </div>
          <div style={{ fontSize:9, color:"#3a5f7a" }}>
            sim time: {(frameRef.current * 0.016 * C.SIM_SPEED).toFixed(1)} s physics
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
          {[
            { label:"PROPELLANT USED",  conv:`${convPropG.toFixed(3)} µg`, ep:`${epPropG.toFixed(3)} µg`, delta:`${saving.toFixed(1)}% SAVING`, good:saving>0 },
            { label:"OVERSHOOT AVG",    conv:`${cv.overshoot_mm.toFixed(3)} mm`, ep:`${ep.overshoot_mm.toFixed(3)} mm`, delta:`${osReduction.toFixed(1)}% REDUCTION`, good:osReduction>0 },
            { label:"CYCLES COMPLETE",  conv:`${cv.cycle_count}`, ep:`${ep.cycle_count}`, delta:"corrections", good:ep.cycle_count <= cv.cycle_count },
            { label:"EFF FACTOR η",     conv:convEta.toFixed(3), ep:epEta.toFixed(3), delta:"lower = better", good:epEta<=convEta },
          ].map(({ label, conv: cval, ep: epval, delta, good }) => (
            <div key={label} style={{ background:"#030d14", borderRadius:6, padding:"10px 12px" }}>
              <div style={{ fontSize:7, color:"#3a5f7a", letterSpacing:1.5, marginBottom:6 }}>{label}</div>
              <div style={{ display:"flex", gap:6, alignItems:"baseline", marginBottom:3 }}>
                <span style={{ fontSize:8, color:ACC1+"88" }}>CONV</span>
                <span style={{ fontSize:12, color:ACC1, fontWeight:700 }}>{cval}</span>
              </div>
              <div style={{ display:"flex", gap:6, alignItems:"baseline", marginBottom:6 }}>
                <span style={{ fontSize:8, color:ACC2+"88" }}>EP</span>
                <span style={{ fontSize:12, color:ACC2, fontWeight:700 }}>{epval}</span>
              </div>
              <div style={{ fontSize:10, color:good?ACC2:RED, fontWeight:700 }}>→ {delta}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Physics constants (collapsible) ── */}
      <div style={{ background:"#050f1a", border:"1px solid #0e2236", borderRadius:8, overflow:"hidden" }}>
        <button
          style={{ width:"100%", background:"transparent", border:"none", padding:"10px 16px",
            cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}
          onClick={() => setShowConst(s=>!s)}>
          <span style={{ fontFamily:MONO, fontSize:9, color:"#3a5f7a", letterSpacing:2 }}>
            ⚙  PHYSICS CONSTANTS
          </span>
          <span style={{ color:"#3a5f7a" }}>{showConst?"▲":"▼"}</span>
        </button>

        {showConst && (
          <div style={{ padding:"12px 16px", borderTop:"1px solid #0e2236" }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 24px" }}>
              <div>
                <Slider label="HULL MASS"         val={C.HULL_MASS}            min={1}   max={50}  step={0.5}
                  fmt={v=>`${v.toFixed(1)} kg`}   onChange={upd("HULL_MASS")}   color={ACC1} />
                <Slider label="EMMS MASS"         val={C.EMMS_MASS}            min={0.1} max={5}   step={0.1}
                  fmt={v=>`${v.toFixed(2)} kg`}   onChange={upd("EMMS_MASS")}   color={ACC3} />
                <Slider label="THRUSTER FORCE"    val={C.THRUSTER_FORCE*1000}  min={1}   max={50}  step={0.5}
                  fmt={v=>`${v.toFixed(1)} mN`}   onChange={v=>upd("THRUSTER_FORCE")(v*1e-3)} color={ACC1} />
                <Slider label="EMMS MAX STROKE"   val={C.EMMS_MAX_STROKE*1000} min={5}   max={100} step={1}
                  fmt={v=>`${v.toFixed(0)} mm`}   onChange={v=>upd("EMMS_MAX_STROKE")(v*1e-3)} color={ACC3} />
                <Slider label="EMMS ACTUATE FORCE" val={C.EMMS_ACTUATE_FORCE}  min={0.1} max={5}   step={0.05}
                  fmt={v=>`${v.toFixed(2)} N`}    onChange={upd("EMMS_ACTUATE_FORCE")} color={ACC3} />
              </div>
              <div>
                <Slider label="EMMS RESET FORCE"  val={C.EMMS_RESET_FORCE*1000} min={1} max={100} step={1}
                  fmt={v=>`${v.toFixed(0)} mN`}   onChange={v=>upd("EMMS_RESET_FORCE")(v*1e-3)} color={ACC2} />
                <Slider label="ISP"               val={C.ISP}                  min={20}  max={300} step={5}
                  fmt={v=>`${v.toFixed(0)} s`}    onChange={upd("ISP")}         color={PUR} />
                <Slider label="SIM SCALE"         val={C.SIM_SCALE}            min={1000} max={40000} step={500}
                  fmt={v=>`${(v/1000).toFixed(0)}k px/m`} onChange={upd("SIM_SCALE")} color={ACC2} />
                <div style={{ fontFamily:MONO, fontSize:8, color:"#3a5f7a", marginTop:10, lineHeight:1.8 }}>
                  TOTAL MASS = {(C.HULL_MASS+C.EMMS_MASS).toFixed(1)} kg<br/>
                  CYCLE STROKE = {((C.EMMS_MASS/(C.HULL_MASS+C.EMMS_MASS))*C.EMMS_MAX_STROKE*2*1000).toFixed(2)} mm/cycle<br/>
                  PROP/PULSE = {((C.THRUSTER_FORCE*C.THRUSTER_MIN_PULSE/(C.ISP*C.G0))*1e9).toFixed(2)} ng<br/>
                  DRAG DECEL = {(C.DRAG_FORCE/(C.HULL_MASS+C.EMMS_MASS)*1e6).toFixed(2)} µm/s² (system)
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
